import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const CTASection = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    studioName: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    const parts = form.name.trim().split(/\s+/);
    const first_name = parts[0] || form.name;
    const last_name = parts.slice(1).join(' ');
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          first_name,
          last_name,
          studio_name: form.studioName,
        }),
      });
      if (!resp.ok) throw new Error('Request failed');
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="request-access" className="relative bg-[#2A2018] text-white pt-20 md:pt-24 pb-16 md:pb-20">
      <div className="absolute -top-24 inset-x-0 h-24 bg-gradient-to-b from-transparent to-[#2A2018] pointer-events-none z-10" />
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16 items-start">
        {/* Left */}
        <div>
          <h2
            className="leading-[1.08] tracking-[-0.02em]"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              fontSize: 'clamp(2.3rem, 4.4vw, 3.45rem)',
            }}
          >
            50 founding members. One window.
          </h2>
          <p className="mt-7 text-[#D6C3A8] text-[15px] leading-[1.7] max-w-[420px]">
            We're not launching publicly. We're starting with a small group whose feedback will shape the product, founding pricing locked for life.
          </p>
        </div>

        {/* Right form */}
        <div>
          {status === 'success' ? (
            <div
              data-testid="cta-success"
              className="border border-[#4A3D31] rounded-xl px-8 py-12 text-center"
            >
              <h3
                className="text-white mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(1.6rem, 2.6vw, 2.2rem)' }}
              >
                Application received.
              </h3>
              <p className="text-[#D6C3A8] text-[15px] leading-relaxed max-w-[420px] mx-auto">
                Thank you, {form.name.trim().split(/\s+/)[0] || 'there'}. We review every studio personally and will reach out within 48 hours.
              </p>
            </div>
          ) : (
          <form onSubmit={onSubmit} data-testid="cta-form">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
              {[
                { key: 'name', label: 'Full name', type: 'text', required: true, placeholder: 'Jane Carter' },
                { key: 'email', label: 'Email address', type: 'email', required: true, placeholder: 'jane@studio.com' },
                { key: 'studioName', label: 'Studio name', type: 'text', placeholder: 'Carter & Co.' },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-[#A89070] text-[11px] tracking-[0.18em] uppercase mb-2">
                    {f.label}
                  </label>
                  <input
                    data-testid={`cta-input-${f.key}`}
                    type={f.type}
                    required={f.required}
                    value={form[f.key]}
                    onChange={onChange(f.key)}
                    placeholder={f.placeholder}
                    className="w-full bg-transparent border border-[#4A3D31] text-white placeholder-[#6B5C4C] text-[15px] px-4 py-3 rounded-md focus:outline-none focus:border-[#C49A57] transition-colors"
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6">
              <button
                type="submit"
                disabled={status === 'submitting'}
                data-testid="cta-submit-btn"
                className="group inline-flex items-center gap-2 bg-[#FAF1E2] text-[#1F1812] hover:bg-white font-semibold text-[16px] rounded-md px-9 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Submitting...' : 'Request your spot'}
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
              <Link
                to="/foundingdesigner"
                className="text-[#A89070] hover:text-[#E8D9C2] text-[14px] font-medium underline-offset-4 hover:underline transition-colors"
              >
                Apply to join the Founding 50
              </Link>
            </div>
            {status === 'error' && (
              <p data-testid="cta-error" className="mt-4 text-[#E5A19A] text-[13px]">
                Something went wrong. Please try again in a moment.
              </p>
            )}
          </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default CTASection;
